/**
 * Decision to execution — the L3 trend card on the decision latency report.
 *
 * PRD: docs/9-exploration/DECISION-LATENCY-INSTRUMENTATION-PRD.md §5.
 * Per cycle with an authorized plan: commitments decided, how many have
 * started (first reached the committed state — owner ruling 2026-10-14), the
 * share, and the median days from the decision to the start.
 *
 * Read-only. Lives inside the decision_latency gate (the page's route gate);
 * execution start is read from commitment states, so when that module is off
 * here the card says so instead of showing a row of zeros that would read as
 * "nothing ever starts".
 */

import { useEffect, useMemo, useState } from 'react';
import { Panel, DataTable, Callout, Skeleton, type Column } from '../../components/ds';
import { useCommitmentStatesEnabled } from '../../hooks/useCommitmentStatesEnabled';
import { fetchDecisionExecution, type DecisionExecutionReport } from '../../lib/decisionExecutionService';
import { formatDays, type CycleExecution } from '../../lib/decisionExecutionCore';

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function DecisionExecutionTrend({ workspaceId, since }: { workspaceId: string | null; since?: string }) {
  const enabled = useCommitmentStatesEnabled();
  const [report, setReport] = useState<DecisionExecutionReport | null>(null);

  useEffect(() => {
    if (!enabled || !workspaceId) return;
    let cancelled = false;
    setReport(null);
    void fetchDecisionExecution(workspaceId, { since }).then((r) => {
      if (!cancelled) setReport(r);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId, since]);

  const columns: Column<CycleExecution>[] = useMemo(
    () => [
      { key: 'name', header: 'Cycle', render: (row) => <span className="text-sm">{row.name}</span> },
      { key: 'authorizedAt', header: 'Plan authorized', render: (row) => <span className="text-sm">{shortDate(row.authorizedAt)}</span> },
      { key: 'decided', header: 'Decided', numeric: true, render: (row) => row.decided },
      {
        key: 'started',
        header: 'Started',
        numeric: true,
        render: (row) => (
          <span>
            {row.started}
            {row.noResourceBasis > 0 && (
              <span className="text-[var(--text-3)]"> · {row.noResourceBasis} can&rsquo;t be seen</span>
            )}
          </span>
        ),
      },
      {
        key: 'ratio',
        header: 'Share started',
        numeric: true,
        render: (row) => (row.ratio === null ? '—' : `${Math.round(row.ratio * 100)}%`),
      },
      {
        key: 'medianDaysToStart',
        header: 'Time to start (median)',
        numeric: true,
        render: (row) => formatDays(row.medianDaysToStart),
      },
    ],
    [],
  );

  if (!enabled) {
    return (
      <Callout tone="neutral" label="Decision to execution" title="Not measured in this workspace">
        <span className="text-sm">
          Execution start is when a commitment first has the resources it needs (backed, not just announced).
          Backing is off in this workspace, so there is nothing to measure it from.
        </span>
      </Callout>
    );
  }

  if (!report) return <Skeleton className="h-32 w-full" />;

  const withUnseen = report.trend.cycles.reduce((n, c) => n + c.noResourceBasis, 0);

  return (
    <Panel label="Decision to execution" title="How much of what was decided has started" count={report.trend.cycles.length} flush>
      <div className="space-y-1 px-4 py-3 text-sm text-[var(--text-2)]">
        <p className="text-[var(--text-1)]">{report.trend.sentence}</p>
        <p className="text-xs text-[var(--text-3)]">
          Decided: the commitments on the cycle&rsquo;s authorized plan. Started: the first time a commitment had the
          resources it needs (backed, not just announced), counted from when its plan was authorized.
          {withUnseen > 0 &&
            ` ${withUnseen} commitment${withUnseen === 1 ? ' has' : 's have'} no work type saying what ${withUnseen === 1 ? 'it needs' : 'they need'}, so ${withUnseen === 1 ? 'its' : 'their'} start can't be seen.`}
        </p>
        {report.error && <p className="text-xs text-[var(--coral)]">Part of this could not be read: {report.error}</p>}
      </div>
      {report.trend.cycles.length > 0 && (
        <DataTable
          columns={columns}
          rows={report.trend.cycles}
          getRowKey={(row) => row.planCycleId}
          density="compact"
          rule="horizontal"
        />
      )}
    </Panel>
  );
}
